import { annotationsService } from '@/services/annotations.service';
import { displayAnnotationAnchor, hideAnnotationAnchor } from '@/util/annotation-anchor-util';
import axios from 'axios';

const initialState = () => ({
  annotations: [],
  selectedAnnotation: null,
  linkedAnnotations: [],
  status: {
    error: null,
    pending: false,
    resolved: false,
    rejected: false,
  },
});

const state = initialState();

const actions = {
  getOpportunityDocumentsAnnotations({ commit }, { opportunityId, cancelToken }) {
    commit('GET_ANNOTATIONS');
    return annotationsService.getOpportunityDocumentAnnotations({ opportunityId, cancelToken }).then(
      (annotations) => {
        commit('GET_ANNOTATIONS_SUCCESS', annotations);
        return annotations;
      },
      (error) => {
        if (!axios.isCancel(error)) {
          console.error(error);
          throw error;
        }
      },
    );
  },
  addDocumentAnnotation({ commit }, { opportunityId, docId, selection }) {
    return annotationsService.addDocumentAnnotation({ opportunityId, docId, selection }).then((createdAnnotation) => {
      commit('ADD_ANNOTATION_SUCCESS', createdAnnotation);
      return createdAnnotation;
    });
  },
  updateDocumentAnnotation({ commit }, { opportunityId, docId, id, payload }) {
    return annotationsService
      .updateDocumentAnnotation({ opportunityId, docId, id, payload })
      .then((updatedAnnotation) => {
        commit('UPDATE_ANNOTATION_SUCCESS', updatedAnnotation);
        return updatedAnnotation;
      });
  },
  deleteDocumentAnnotation({ commit }, { opportunityId, docId, id }) {
    return annotationsService.deleteDocumentAnnotation({ opportunityId, docId, id }).then(() => {
      if (state.linkedAnnotations.find((annotation) => annotation.id == id)) {
        commit('DELETE_LINKED_ANNOTATION', { textAnnotationId: id, value: null });
      }
      commit('DELETE_ANNOTATION_SUCCESS', id);
    });
  },
  setSelectedAnnotation({ commit, state }, annotation) {
    if (state.selectedAnnotation !== null) commit('RESET_SELECTED_ANNOTATION');
    commit('SET_SELECTED_ANNOTATION', annotation);
  },
  resetSelectedAnnotation({ commit }) {
    commit('RESET_SELECTED_ANNOTATION');
  },
  RESET({ commit }) {
    commit('RESET_STORE');
  },
};

const mutations = {
  GET_ANNOTATIONS(state) {
    state.status = {
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  GET_ANNOTATIONS_SUCCESS(state, annotations) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
    state.annotations = annotations;
  },
  ADD_ANNOTATION_SUCCESS(state, createdAnnotation) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
    state.annotations.push(createdAnnotation);
  },
  DELETE_ANNOTATION_SUCCESS(state, id) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };

    const annotationIndex = state.annotations.findIndex((annotation) => annotation.id === id);
    if (annotationIndex === -1) return;
    state.annotations.splice(annotationIndex, 1);
  },
  UPDATE_ANNOTATION_SUCCESS(state, updatedAnnotation) {
    const annotationIndex = state.annotations.findIndex((annotation) => annotation.id === updatedAnnotation.id);
    if (annotationIndex === -1) return;
    const mergedAnnotation = { ...state.annotations[annotationIndex], ...updatedAnnotation };
    state.annotations.splice(annotationIndex, 1, mergedAnnotation);
    if (state.selectedAnnotation && state.selectedAnnotation.id === updatedAnnotation.id) {
      state.selectedAnnotation = { ...state.selectedAnnotation, ...updatedAnnotation };
    }
  },
  SET_SELECTED_ANNOTATION(state, annotation) {
    state.selectedAnnotation = annotation;

    if (!annotation.id) return;

    let annotationElements = document.querySelectorAll(`.annotation-${annotation.id}`);
    let annotationAnchorElement = document.querySelector(`.annotation-anchor-${annotation.id}`);
    if (annotationElements.length > 0) {
      annotationElements.forEach((element) => {
        element.classList.add('selected');
      });
      if (annotationAnchorElement) annotationAnchorElement.classList.add('selected');
    }
  },
  SET_LINKED_ANNOTATIONS(state, evaluationGrid) {
    state.linkedAnnotations = [];
    evaluationGrid.fields.forEach((field) => {
      field.values.forEach((value) => {
        if (value.textAnnotationId) {
          const annotation = state.linkedAnnotations.find(
            (annotation) => annotation.textAnnotationId === value.textAnnotationId,
          );
          // If there is already a link from this annotation, then we want to add a new value to the pair
          if (annotation) {
            annotation.values.push(value);
          } else {
            // Otherwise, we add a new key-values pair
            state.linkedAnnotations.push({ textAnnotationId: value.textAnnotationId, values: [value] });
          }
        }
      });
    });
    // Show anchors for annotations that were already rendered before linkedAnnotations was populated
    state.linkedAnnotations.forEach((annotation) => {
      displayAnnotationAnchor(annotation.textAnnotationId);
    });
  },
  ADD_LINKED_ANNOTATION(state, { textAnnotationId, value }) {
    const annotation = state.linkedAnnotations.find((annotation) => annotation.textAnnotationId == textAnnotationId);

    // If there is already a link from this annotation, then we want to add a new value to the pair
    if (annotation) {
      annotation.values.push(value);
    } else {
      // Otherwise, we add a new key-values pair
      state.linkedAnnotations.push({ textAnnotationId: textAnnotationId, values: [value] });
    }
    displayAnnotationAnchor(textAnnotationId);
  },
  DELETE_LINKED_ANNOTATION(state, { textAnnotationId, value }) {
    const annotationIndex = state.linkedAnnotations.findIndex(
      (annotation) => annotation.textAnnotationId === textAnnotationId,
    );
    if (annotationIndex === -1) return;
    const annotation = state.linkedAnnotations[annotationIndex];
    if (annotation.values.length > 1) {
      const valueIndex = state.linkedAnnotations[annotationIndex].values.findIndex((v) => v.id === value.id);
      if (valueIndex === -1) return;
      state.linkedAnnotations[annotationIndex].values.splice(valueIndex, 1);
      return;
    }
    hideAnnotationAnchor(textAnnotationId);
    state.linkedAnnotations.splice(annotationIndex, 1);
  },
  RESET_SELECTED_ANNOTATION(state) {
    const annotation = state.selectedAnnotation;
    if (annotation) {
      let annotationElement = document.querySelector(`.annotation-${annotation.id}`);
      let annotationAnchorElement = document.querySelector(`.annotation-anchor-${annotation.id}`);
      if (annotationElement) annotationElement.classList.remove('selected');
      if (annotationAnchorElement) annotationAnchorElement.classList.remove('selected');
    }
    state.selectedAnnotation = null;
  },
  RESET_STORE(state) {
    const initial = initialState();
    Object.keys(initial).forEach((key) => {
      state[key] = initial[key];
    });
  },
};

const getters = {
  annotations: (state) => state.annotations,
  selectedAnnotation: (state) => state.selectedAnnotation,
  linkedAnnotations: (state) => state.linkedAnnotations,

  // status
  error: (state) => state.status.error,
  pending: (state) => state.status.pending,
  rejected: (state) => state.status.rejected,
  resolved: (state) => state.status.resolved,
};

export const annotations = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
